import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Layout, Form, Select, Input, Button, Table, Space, Tag, message, Popconfirm, Tooltip, Row, Col } from 'antd';
import { SearchOutlined, PlusOutlined, EditOutlined, DeleteOutlined, EyeOutlined, DownloadOutlined, CopyOutlined, SortAscendingOutlined, SortDescendingOutlined } from '@ant-design/icons';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import api from '../utils/api';
import { ensureUploadMode, getUploadMode } from '../utils/uploadMode';
import { downloadOssFile, previewOssPdf } from '../utils/ossUpload';
import AppHeader from '../components/AppHeader';
import { useTheme } from '../context/ThemeContext';

const { Content } = Layout;
const { Option } = Select;

const injectCompactStyle = () => {
  const styleId = 'compact-table-style';
  if (!document.getElementById(styleId)) {
    const style = document.createElement('style');
    style.id = styleId;
    style.innerHTML = `
      .compact-table .ant-table-thead > tr > th {
        padding: 6px 6px !important;
        font-size: 13px;
        white-space: nowrap;
      }
      .compact-table .ant-table-tbody > tr > td {
        padding: 4px 6px !important;
        font-size: 13px;
      }
      .compact-table .ant-table-cell {
        line-height: 1.4 !important;
      }
    `;
    document.head.appendChild(style);
  }
};

const formatDate = (text) => {
  if (!text) return '-';
  const date = new Date(text);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// 将探头外形尺寸从存储格式（高*长*宽）转换为显示格式（长*宽*高）
const formatProbeOuterSize = (text) => {
  if (!text || text === '-') return text;
  const parts = text.split('*');
  if (parts.length !== 3) return text;
  // 存储格式: height*length*width
  // 显示格式: length*width*height
  return `${parts[1]}*${parts[2]}*${parts[0]}`;
};

const DrawingListPage = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { deviceType } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [config, setConfig] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [sortBy, setSortBy] = useState('');
  
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const canEdit = ['designer', 'designer_leader', 'admin'].includes(user.role);
  const isSales = user.role === 'sales';
  const { isDarkMode, getBackgroundColor, getTextColor } = useTheme();

  const initializedRef = useRef(false);
  const requestParamsRef = useRef({});

  useEffect(() => {
    injectCompactStyle();
  }, []);

  useEffect(() => {
    if (deviceType) {
      api.get(`/device-types/${deviceType}`).then(res => {
        setConfig(res.data);
      }).catch(() => {
        message.error('设备类型配置加载失败');
      });
    }
  }, [deviceType]);

  useEffect(() => {
    if (initializedRef.current) return;
    
    const page = parseInt(searchParams.get('page')) || 1;
    const pageSizeParam = parseInt(searchParams.get('pageSize')) || 10;
    const sort = searchParams.get('sort_by') || '';
    
    setCurrentPage(page);
    setPageSize(pageSizeParam);
    setSortBy(sort);

    const formValues = {};
    const filterFields = [
      'drawing_no', 'production_notice', 'creator', 'frame_type', 'probe_type', 'probe_size',
      'passable_size', 'probe_outer_size', 'frame_length', 'machine_outer_size',
      'belt_height', 'belt_direction', 'control_box_design', 'alarm_reject_mode',
      'belt_thickness', 'belt_material', 'other_features'
    ];
    filterFields.forEach(field => {
      const value = searchParams.get(field);
      if (value !== null && value !== '') {
        formValues[field] = value;
      }
    });
    
    if (Object.keys(formValues).length > 0) {
      form.setFieldsValue(formValues);
    }

    initializedRef.current = true;
  }, [searchParams, form]);

  const fetchData = useCallback(async (overrideParams = null) => {
    if (!deviceType) return;
    setLoading(true);
    try {
      const params = overrideParams || requestParamsRef.current;
      
      const queryParams = {
        page: params.page || currentPage,
        pageSize: params.pageSize || pageSize,
        sort_by: params.sort_by !== undefined ? params.sort_by : sortBy,
        ...params.filters
      };
      
      const res = await api.get(`/drawings/${deviceType}`, { params: queryParams });
      setData(res.data.data);
      setCurrentPage(res.data.page);
      setPageSize(res.data.pageSize);
      setTotal(res.data.total);
    } catch (err) {
      message.error('获取数据失败');
    } finally {
      setLoading(false);
    }
  }, [deviceType, currentPage, pageSize, sortBy]);

  useEffect(() => {
    if (!initializedRef.current || !config) return;
    
    const values = form.getFieldsValue();
    const cleanValues = Object.fromEntries(
      Object.entries(values).filter(([_, v]) => v !== undefined && v !== '' && v !== null)
    );
    
    const page = parseInt(searchParams.get('page')) || 1;
    const pageSizeParam = parseInt(searchParams.get('pageSize')) || 10;
    const sort = searchParams.get('sort_by') || '';
    
    const params = {
      page,
      pageSize: pageSizeParam,
      sort_by: sort,
      filters: cleanValues
    };
    
    requestParamsRef.current = params;
    fetchData(params);
    
    initializedRef.current = false;
  }, [config, searchParams, form, fetchData]);

  const syncFiltersToURL = (filters, page = currentPage, pageSizeParam = pageSize, sort = sortBy) => {
    const newParams = new URLSearchParams();
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== '' && value !== null) {
        newParams.set(key, value);
      }
    });
    
    if (page > 1) newParams.set('page', String(page));
    if (pageSizeParam !== 10) newParams.set('pageSize', String(pageSizeParam));
    if (sort) newParams.set('sort_by', sort);
    
    setSearchParams(newParams, { replace: true });
  };

  const handleSearch = (values) => {
    const cleanValues = Object.fromEntries(
      Object.entries(values).filter(([_, v]) => v !== undefined && v !== '' && v !== null)
    );
    const newPage = 1;
    setCurrentPage(newPage);
    syncFiltersToURL(cleanValues, newPage, pageSize, sortBy);
    
    const params = {
      page: newPage,
      pageSize,
      sort_by: sortBy,
      filters: cleanValues
    };
    requestParamsRef.current = params;
    fetchData(params);
  };

  const handleReset = () => {
    form.resetFields();
    setSortBy('');
    setCurrentPage(1);
    setSearchParams(new URLSearchParams(), { replace: true });
    
    const params = {
      page: 1,
      pageSize: 10,
      sort_by: '',
      filters: {}
    };
    requestParamsRef.current = params;
    fetchData(params);
  };

  const handleTableChange = (newPagination) => {
    const values = form.getFieldsValue();
    const cleanValues = Object.fromEntries(
      Object.entries(values).filter(([_, v]) => v !== undefined && v !== '' && v !== null)
    );
    
    const newPage = newPagination.current;
    const newPageSize = newPagination.pageSize;
    
    setCurrentPage(newPage);
    setPageSize(newPageSize);
    syncFiltersToURL(cleanValues, newPage, newPageSize, sortBy);
    
    const params = {
      page: newPage,
      pageSize: newPageSize,
      sort_by: sortBy,
      filters: cleanValues
    };
    requestParamsRef.current = params;
    fetchData(params);
  };

  const toggleSort = () => {
    let newSort = '';
    if (sortBy === '') {
      newSort = 'created_at_desc';
    } else if (sortBy === 'created_at_desc') {
      newSort = 'created_at_asc';
    } else {
      newSort = '';
    }
    setSortBy(newSort);
    setCurrentPage(1);
    
    const values = form.getFieldsValue();
    const cleanValues = Object.fromEntries(
      Object.entries(values).filter(([_, v]) => v !== undefined && v !== '' && v !== null)
    );
    
    syncFiltersToURL(cleanValues, 1, pageSize, newSort);
    
    const params = {
      page: 1,
      pageSize,
      sort_by: newSort,
      filters: cleanValues
    };
    requestParamsRef.current = params;
    fetchData(params);
  };

  const handleDelete = async (record) => {
    try {
      const res = await api.post(`/drawing-approvals/delete/${deviceType}/${record.id}`, {
        reason: '申请删除图纸记录'
      });
      message.success(res.data.message || '删除申请已提交，等待领导审核');
      fetchData(requestParamsRef.current);
    } catch (err) {
      message.error(err.response?.data?.message || '提交失败');
    }
  };

  const handleClone = (record) => {
    navigate(`/drawings/${deviceType}/new?cloneFrom=${record.id}`);
  };

  const handleDownload = async (record, fileType) => {
    try {
      if (fileType === 'three_d_stp') {
        const statusRes = await api.get(`/drawing-approvals/download-3d-status/${deviceType}/${record.id}`);
        const { hasApproval, status: approvalStatus, downloaded } = statusRes.data;

        if (downloaded) {
          const submitRes = await api.post(`/drawing-approvals/download-3d/${deviceType}/${record.id}`);
          if (submitRes.data.approved) {
            message.info(submitRes.data.message);
          } else {
            message.info('该3D图已下载过，已重新提交下载审批申请，请等待审核通过后下载');
            return;
          }
        } else if (!hasApproval || (!approvalStatus || approvalStatus === 'rejected')) {
          const submitRes = await api.post(`/drawing-approvals/download-3d/${deviceType}/${record.id}`);
          if (submitRes.data.approved) {
            message.info(submitRes.data.message);
          } else {
            message.info(submitRes.data.message);
            return;
          }
        } else if (approvalStatus === 'pending') {
          message.info('3D图下载申请正在审批中，请耐心等待审批通过后再下载');
          return;
        }
      }

      const fileName = record[`${fileType}_name`] || `${record.drawing_no}_${fileType}`;
      await ensureUploadMode();
      if (getUploadMode() === 'oss') {
        await downloadOssFile(`/drawings/${deviceType}/${record.id}/download/${fileType}`, fileName);
      } else {
        const res = await api.get(`/drawings/${deviceType}/${record.id}/download/${fileType}`, {
          responseType: 'blob'
        });
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      }
      message.success('下载成功');
    } catch (err) {
      const errMsg = err.response?.data?.message || '下载失败';
      message.error(errMsg);
    }
  };

  const handlePreview = async (record, fileType) => {
    try {
      if (fileType === 'three_d_stp') {
        message.info('STP文件不支持浏览器预览，请下载后使用专业软件查看');
        return;
      }

      const fileName = record[`${fileType}_name`] || '';
      const ext = fileName.slice(fileName.lastIndexOf('.')).toLowerCase();
      
      if (ext === '.pdf') {
        await ensureUploadMode();
        let previewWindow;
        if (getUploadMode() === 'oss') {
          previewWindow = await previewOssPdf(`/drawings/${deviceType}/${record.id}/preview/${fileType}`);
        } else {
          const response = await api.get(
            `/drawings/${deviceType}/${record.id}/preview/${fileType}`,
            { responseType: 'blob' }
          );
          const blob = new Blob([response.data], {
            type: response.headers['content-type'] || 'application/octet-stream'
          });
          const url = window.URL.createObjectURL(blob);
          previewWindow = window.open(url, '_blank');
          setTimeout(() => window.URL.revokeObjectURL(url), 30000);
        }
        if (!previewWindow) {
          message.warning('请允许浏览器弹窗以查看预览');
        }
        return;
      }

      message.info('该文件格式不支持浏览器预览，请下载后使用专业软件查看');
    } catch (err) {
      const errMsg = err.response?.data?.message || '预览失败，请检查登录状态';
      message.error(errMsg);
    }
  };

  const renderFileCell = (record, fileType) => {
    const fileField = `${fileType}_file`;
    const nameField = `${fileType}_name`;
    const hasFile = !!record[fileField];
    const fileName = record[nameField] || '';

    if (isSales && config && config.salesVisibleFileTypes && !config.salesVisibleFileTypes.includes(fileType)) {
      return <Tag>无权访问</Tag>;
    }

    if (!hasFile) {
      return <Tag color="default" style={{ fontSize: 12, padding: '0 4px' }}>未上传</Tag>;
    }

    const ext = fileName.slice(fileName.lastIndexOf('.')).toLowerCase();
    const canPreview = ext === '.pdf';
    const isStp = ext === '.stp' || ext === '.step';

    return (
      <Space direction="vertical" size={2} style={{ width: '100%' }}>
        <Tooltip title={fileName}>
          <div style={{ 
            fontSize: 12, 
            color: isDarkMode ? 'rgba(255,255,255,0.65)' : '#666', 
            maxWidth: 110, 
            overflow: 'hidden', 
            textOverflow: 'ellipsis', 
            whiteSpace: 'nowrap' 
          }}>
            {fileName}
          </div>
        </Tooltip>
        <Space size={2}>
          {canPreview && (
            <Button 
              size="small" 
              icon={<EyeOutlined style={{ fontSize: 11 }} />}
              style={{ fontSize: 11, padding: '0 4px', height: 20 }}
              onClick={() => handlePreview(record, fileType)}
            >
              预览
            </Button>
          )}
          <Button 
            size="small" 
            icon={<DownloadOutlined style={{ fontSize: 11 }} />}
            style={{ fontSize: 11, padding: '0 4px', height: 20 }}
            onClick={() => handleDownload(record, fileType)}
          >
            下载
          </Button>
        </Space>
      </Space>
    );
  };

  const getSortIcon = () => {
    if (sortBy === 'created_at_desc') return <SortDescendingOutlined />;
    if (sortBy === 'created_at_asc') return <SortAscendingOutlined />;
    return <SortDescendingOutlined style={{ color: '#999' }} />;
  };

  const getSortText = () => {
    if (sortBy === 'created_at_desc') return '创建时间↓';
    if (sortBy === 'created_at_asc') return '创建时间↑';
    return '创建时间排序';
  };

  const goToDetail = (recordId) => {
    navigate(`/drawings/${deviceType}/detail/${recordId}?${searchParams.toString()}`);
  };

  if (!config) return (
    <Layout style={{ minHeight: '100vh', background: getBackgroundColor() }}>
      <AppHeader />
      <Content style={{ padding: 16, color: getTextColor() }}>加载中...</Content>
    </Layout>
  );

  const columns = [
    { 
      title: '图纸编号', 
      dataIndex: 'drawing_no', 
      width: 125,
      fixed: 'left',
      align: 'center',
      render: (text, record) => (
        <Button 
          type="link" 
          onClick={() => goToDetail(record.id)}
          style={{ padding: 0, height: 'auto' }}
        >
          {text}
        </Button>
      )
    },
    { title: '生产通知单', dataIndex: 'production_notice', width: 105, align: 'center', ellipsis: true },
    { title: '制图人', dataIndex: 'creator', width: 75, align: 'center' },
    { 
      title: '制图日期', 
      dataIndex: 'draw_date', 
      width: 95,
      align: 'center',
      render: (text) => formatDate(text)
    },
    { title: '机架类型', dataIndex: 'frame_type', width: 85, align: 'center' },
    { title: '探头类型', dataIndex: 'probe_type', width: 85, align: 'center' },
    { title: '探头开口尺寸', dataIndex: 'probe_size', width: 100, align: 'center', ellipsis: true },
    { title: '可通过尺寸', dataIndex: 'passable_size', width: 100, align: 'center', ellipsis: true },
    { title: '探头外形尺寸', dataIndex: 'probe_outer_size', width: 115, align: 'center', ellipsis: true, render: (text) => formatProbeOuterSize(text) },
    { title: '机架长度', dataIndex: 'frame_length', width: 80, align: 'center' },
    { title: '整机外形尺寸', dataIndex: 'machine_outer_size', width: 115, align: 'center', ellipsis: true },
    { title: '皮带厚度', dataIndex: 'belt_thickness', width: 80, align: 'center' },
    { title: '皮带材质', dataIndex: 'belt_material', width: 80, align: 'center' },
    { title: '皮带输送面离地高度', dataIndex: 'belt_height', width: 130, align: 'center', ellipsis: true },
    { title: '皮带运行方向', dataIndex: 'belt_direction', width: 100, align: 'center' },
    { title: '控制箱与机身设计', dataIndex: 'control_box_design', width: 110, align: 'center' },
    { title: '报警剔除方式', dataIndex: 'alarm_reject_mode', width: 100, align: 'center', ellipsis: true },
    { 
      title: '其他特点', 
      dataIndex: 'other_features', 
      width: 60,
      align: 'center',
      render: (text) => (
        <Tooltip title={text || '-'}>
          <span style={{ 
            display: 'inline-block', 
            maxWidth: 60, 
            overflow: 'hidden', 
            textOverflow: 'ellipsis', 
            whiteSpace: 'nowrap' 
          }}>
            {text || '-'}
          </span>
        </Tooltip>
      )
    },
    ...config.fileTypes.map(ft => ({
      title: ft.label,
      width: 130,
      align: 'center',
      render: (_, record) => renderFileCell(record, ft.key)
    })),
    {
      title: '创建时间',
      dataIndex: 'created_at',
      width: 95,
      align: 'center',
      render: (text) => formatDate(text)
    },
    {
      title: '操作',
      fixed: 'right',
      width: 150,
      align: 'center',
      render: (_, record) => (
        <Space size={2} wrap>
          {canEdit && (
            <Button 
              size="small" 
              icon={<EditOutlined style={{ fontSize: 11 }} />}
              style={{ fontSize: 11, padding: '0 4px', height: 20 }}
              onClick={() => navigate(`/drawings/${deviceType}/edit/${record.id}`)}
            >
              修改
            </Button>
          )}
          {canEdit && (
            <Button 
              size="small" 
              icon={<CopyOutlined style={{ fontSize: 11 }} />}
              style={{ fontSize: 11, padding: '0 4px', height: 20 }}
              onClick={() => handleClone(record)}
            >
              复制
            </Button>
          )}
          {canEdit && (
            <Popconfirm
              title="确定要申请删除这条记录吗？"
              description="删除需要领导审核同意，审核通过后将删除该图纸及其所有关联文件"
              onConfirm={() => handleDelete(record)}
              okText="确定"
              cancelText="取消"
            >
              <Button 
                size="small" 
                danger 
                icon={<DeleteOutlined style={{ fontSize: 11 }} />}
                style={{ fontSize: 11, padding: '0 4px', height: 20 }}
              >
                删除
              </Button>
            </Popconfirm>
          )}
        </Space>
      )
    }
  ];

  return (
    <Layout style={{ minHeight: '100vh', background: getBackgroundColor() }}>
      <AppHeader />
      <Content style={{ padding: 16 }}>
        <h2 style={{ marginBottom: 16, fontSize: 18, fontWeight: 600, color: isDarkMode ? '#fff' : '#000' }}>
          {config.name}图纸库
        </h2>
        
        <Form 
          form={form} 
          layout="inline" 
          onFinish={handleSearch} 
          style={{ 
            marginBottom: 16, 
            background: isDarkMode ? '#1f1f1f' : '#f5f5f5', 
            padding: '16px 20px', 
            borderRadius: 8,
            border: isDarkMode ? '1px solid #434343' : '1px solid #d9d9d9'
          }}
        >
          <Row gutter={[16, 12]} align="middle">
            <Col>
              <Form.Item name="drawing_no" label="图纸编号" style={{ marginBottom: 0 }}>
                <Input placeholder="支持模糊查询" style={{ width: 160 }} allowClear />
              </Form.Item>
            </Col>
            <Col>
              <Form.Item name="production_notice" label="生产通知单" style={{ marginBottom: 0 }}>
                <Input placeholder="支持模糊查询" style={{ width: 160 }} allowClear />
              </Form.Item>
            </Col>
            <Col>
              <Form.Item name="creator" label="制图人" style={{ marginBottom: 0 }}>
                <Input placeholder="输入制图人" style={{ width: 120 }} allowClear />
              </Form.Item>
            </Col>
            <Col>
              <Form.Item name="frame_type" label="机架类型" style={{ marginBottom: 0 }}>
                <Select placeholder="请选择" allowClear style={{ width: 120 }}>
                  {['轻便型', '承重型', '小辊筒', '饼干机', '轻便剔除型'].map(o => (
                    <Option key={o} value={o}>{o}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col>
              <Form.Item name="probe_type" label="探头类型" style={{ marginBottom: 0 }}>
                <Select placeholder="请选择" allowClear style={{ width: 120 }}>
                  {['C1老款', 'C1新款', 'C2老款', 'C2新款', 'K1', 'K2', 'D'].map(o => (
                    <Option key={o} value={o}>{o}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col>
              <Form.Item name="probe_size" label="探头开口尺寸" style={{ marginBottom: 0 }}>
                <Input placeholder="如5025" style={{ width: 120 }} allowClear />
              </Form.Item>
            </Col>
            <Col>
              <Form.Item name="passable_size" label="可通过尺寸" style={{ marginBottom: 0 }}>
                <Input placeholder="如450*220" style={{ width: 120 }} allowClear />
              </Form.Item>
            </Col>
            <Col>
              <Form.Item name="probe_outer_size" label="探头外形尺寸" style={{ marginBottom: 0 }}>
                <Input placeholder="如1090*310*430" style={{ width: 140 }} allowClear />
              </Form.Item>
            </Col>
            <Col>
              <Form.Item name="frame_length" label="机架长度" style={{ marginBottom: 0 }}>
                <Input placeholder="如1600" style={{ width: 100 }} allowClear />
              </Form.Item>
            </Col>
            <Col>
              <Form.Item name="machine_outer_size" label="整机外形尺寸" style={{ marginBottom: 0 }}>
                <Input placeholder="如1800*1090*1020" style={{ width: 140 }} allowClear />
              </Form.Item>
            </Col>
            <Col>
              <Form.Item name="belt_thickness" label="皮带厚度" style={{ marginBottom: 0 }}>
                <Select placeholder="请选择" allowClear style={{ width: 100 }}>
                  <Option value="1.3">1.3mm</Option>
                  <Option value="1.5">1.5mm</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col>
              <Form.Item name="belt_material" label="皮带材质" style={{ marginBottom: 0 }}>
                <Select placeholder="请选择" allowClear style={{ width: 100 }}>
                  <Option value="PVC">PVC</Option>
                  <Option value="PU">PU</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col>
              <Form.Item name="belt_height" label="皮带输送面离地高度" style={{ marginBottom: 0 }}>
                <Input placeholder="如700±30" style={{ width: 140 }} allowClear />
              </Form.Item>
            </Col>
            <Col>
              <Form.Item name="belt_direction" label="皮带运行方向" style={{ marginBottom: 0 }}>
                <Select placeholder="请选择" allowClear style={{ width: 120 }}>
                  <Option value="从左向右">从左向右</Option>
                  <Option value="从右向左">从右向左</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col>
              <Form.Item name="control_box_design" label="控制箱与机身设计" style={{ marginBottom: 0 }}>
                <Select placeholder="请选择" allowClear style={{ width: 120 }}>
                  <Option value="一体设计">一体设计</Option>
                  <Option value="分体设计">分体设计</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col>
              <Form.Item name="alarm_reject_mode" label="报警剔除方式" style={{ marginBottom: 0 }}>
                <Select placeholder="请选择" allowClear style={{ width: 140 }}>
                  {['报警停机', '报警倒带', '报警不停机', '推杆剔除', '翻板剔除', '气吹剔除', '拨杆剔除', '一体式下沉剔除', '单段下沉剔除', '左右剔除', '伸缩剔除', '滚筒无杆气缸剔除'].map(o => (
                    <Option key={o} value={o}>{o}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col>
              <Form.Item name="other_features" label="其他特点" style={{ marginBottom: 0 }}>
                <Input placeholder="输入关键词" style={{ width: 120 }} allowClear />
              </Form.Item>
            </Col>
            <Col>
              <Form.Item style={{ marginBottom: 0 }}>
                <Space align="center">
                  <Button type="primary" htmlType="submit" icon={<SearchOutlined />} style={{ width: 80, height: 32, padding: 0 }}>
                    筛选
                  </Button>
                  <Button onClick={handleReset} style={{ width: 80, height: 32, padding: 0 }}>
                    重置
                  </Button>
                  <Button onClick={toggleSort} icon={getSortIcon()} style={{ height: 32 }}>
                    {getSortText()}
                  </Button>
                </Space>
              </Form.Item>
            </Col>
          </Row>
        </Form>

        {canEdit && (
          <div style={{ marginBottom: 12 }}>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate(`/drawings/${deviceType}/new`)}>
              新增图纸
            </Button>
          </div>
        )}

        <Table
          columns={columns}
          dataSource={data}
          rowKey="id"
          loading={loading}
          size="small"
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: total,
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 条记录`
          }}
          onChange={handleTableChange}
          scroll={{ x: 'max-content' }}
          bordered
          className="compact-table"
        />
      </Content>
    </Layout>
  );
};

export default DrawingListPage;
